// pkg169 — status badges for the async-extraction states 'processing' and 'failed' — delivered 2026-09-10
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';


// Lists every discovery response across the firm. Filter by client.
// Each row is clickable → opens the workspace page.

const STATUS_STYLES = {
  draft: { bg: '#fdf6e3', fg: '#b7870a', border: '#f0d68a', label: 'Draft' },
  visible_to_client: { bg: '#e8f0fb', fg: '#2f5496', border: '#bcd4ee', label: 'Client editing' },
  locked: { bg: '#e8f7ef', fg: '#1a7a5e', border: '#b8e3cf', label: 'Locked' },
  processing: { bg: '#eef0f6', fg: '#1E2761', border: '#c9cfe3', label: 'Processing' }, // pkg169
  failed: { bg: '#fbeae7', fg: '#B85042', border: '#eec3bb', label: 'Failed' }          // pkg169
};

const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 };
const td = { padding: '10px 12px', fontFamily: 'Arial, sans-serif', fontSize: 13 };

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      fontFamily: 'Arial, sans-serif', display: 'inline-block', whiteSpace: 'nowrap'
    }}>{s.label}</span>);

}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminDiscoveryResponseListPage() {
  const navigate = useNavigate();

  const [responses, setResponses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [clientFilter, setClientFilter]     = useState('');
  const clientPickerRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const filteredClients = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    const sorted = [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!q) return sorted;
    return sorted.filter((c) => {
      const hay = [c.name, c.email, c.display_name, c.case_description, c.clio_matter_number]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [clients, clientFilter]);

  const selectedClient = clients.find((c) => c.id === filterClientId);

  useEffect(() => {
    adminApi.listClients().then(setClients).catch(() => {});
    load('');
  }, []);

  async function load(clientId) {
    setLoading(true);setError('');
    try {
      const data = await adminApi.listDiscoveryResponses(clientId || undefined);
      setResponses(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function onFilterChange(v) {
    setFilterClientId(v);
    setClientFilter('');
    load(v);
  }

  // Danger-zone delete: the user must type the client name (or "DELETE" if the
  // response has no client name) exactly before the confirm button enables.
  const requiredText = deleteTarget ? ((deleteTarget.client_name || '').trim() || 'DELETE') : '';
  const canConfirmDelete = !!deleteTarget && confirmText.trim() === requiredText;

  function openDelete(r, e) {
    if (e) e.stopPropagation();
    setError('');
    setConfirmText('');
    setDeleteTarget(r);
  }
  function closeDelete() {
    if (deleting) return;
    setDeleteTarget(null);
    setConfirmText('');
  }
  async function handleDelete() {
    if (!canConfirmDelete) return;
    setDeleting(true);
    setError('');
    try {
      await adminApi.deleteDiscoveryResponse(deleteTarget.id);
      setDeleteTarget(null);
      setConfirmText('');
      await load(filterClientId);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h1 className="page-title">Discovery Responses</h1>
          <p className="page-subtitle">Upload opposing counsel's discovery, work through the questions with your client, and generate the response document.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/admin/dashboard/discovery-responses/new')}>
          + New Discovery Response
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Filter */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ fontSize: 13, marginBottom: 10 }}>Filter by Client</div>
        {!filterClientId ? (
          <div>
            <input
              ref={clientPickerRef}
              type="text"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              placeholder="Type to search by name, email, or matter…"
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            {clients.length > 0 && (
              <div style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
                <div
                  onClick={() => onFilterChange('')}
                  style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888', background: '#fafafa' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#eef4fb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fafafa'}
                >
                  Show all clients
                </div>
                {filteredClients.map((c, i) => (
                  <div key={c.id}
                    onClick={() => onFilterChange(c.id)}
                    style={{
                      padding: '10px 12px',
                      borderBottom: i === filteredClients.length - 1 ? 'none' : '1px solid #f0f0f0',
                      cursor: 'pointer',
                      background: i % 2 === 0 ? '#fff' : '#fafafa',
                      fontFamily: 'Arial, sans-serif',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#eef4fb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                  >
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                    {c.case_description && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{c.case_description}</div>}
                  </div>
                ))}
                {filteredClients.length === 0 && clientFilter && (
                  <div style={{ padding: 14, textAlign: 'center', color: '#888', fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
                    No clients match &ldquo;{clientFilter}&rdquo;
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontFamily: 'Arial, sans-serif', fontWeight: 600 }}>{selectedClient?.name}</div>
              {selectedClient?.case_description && (
                <div style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 2 }}>{selectedClient.case_description}</div>
              )}
            </div>
            <button className="btn btn-sm" onClick={() => { onFilterChange(''); setTimeout(() => clientPickerRef.current?.focus(), 50); }}>
              Clear filter
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ?
      <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading...</div> :
      responses.length === 0 ?
      <div className="card" style={{ textAlign: 'center', color: '#888', padding: 32, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
          No discovery responses yet. Click "+ New Discovery Response" to upload opposing counsel's document.
        </div> :

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0F1838' }}>
                  <th style={th}>Client</th>
                  <th style={th}>Title</th>
                  <th style={{ ...th, width: 130 }}>Status</th>
                  <th style={{ ...th, width: 110, textAlign: 'right' }}>Questions</th>
                  <th style={{ ...th, width: 110, textAlign: 'right' }}>Approved</th>
                  <th style={{ ...th, width: 130 }}>Uploaded</th>
                  <th style={{ ...th, width: 90, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {responses.map((r, i) =>
              <tr key={r.id}
              onClick={() => navigate(`/admin/dashboard/discovery-responses/${r.id}`)}
              style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.client_name}</td>
                    <td style={td}>{r.title || <span style={{ color: '#aaa' }}>(untitled)</span>}</td>
                    <td style={td}><StatusBadge status={r.status} /></td>
                    <td style={{ ...td, textAlign: 'right' }}>{r.question_count ?? 0}</td>
                    <td style={{ ...td, textAlign: 'right', color: r.approved_count === r.question_count && r.question_count > 0 ? '#1a7a5e' : '#555' }}>
                      {r.approved_count ?? 0} / {r.question_count ?? 0}
                    </td>
                    <td style={{ ...td, color: '#555' }}>{fmtDate(r.created_at)}</td>
                    <td style={{ ...td, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-sm" onClick={(e) => openDelete(r, e)}
                        style={{ color: '#B85042', borderColor: '#e3b4ae' }}>Delete</button>
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
          </div>
        </div>
      }

      {/* Danger-zone delete confirmation */}
      {deleteTarget &&
        <div onClick={closeDelete}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 6, maxWidth: 460, width: '100%', padding: 20, fontFamily: 'Arial, sans-serif' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 18, color: '#B85042' }}>Delete discovery response</h2>
            <p style={{ fontSize: 13, color: '#444', lineHeight: 1.5, marginTop: 0 }}>
              This permanently deletes the response for <strong>{deleteTarget.client_name || '(no client)'}</strong>
              {deleteTarget.title ? <> — <em>{deleteTarget.title}</em></> : null}, including all of its
              questions and answers. This cannot be undone.
            </p>
            <div style={{ background: '#fdecea', border: '1px solid #f3c0ba', borderRadius: 4, padding: 12, margin: '12px 0' }}>
              <label style={{ fontSize: 12, color: '#7a2118', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                Type <code style={{ background: '#fff', padding: '1px 5px', borderRadius: 3, border: '1px solid #eccac4' }}>{requiredText}</code> to confirm
              </label>
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && canConfirmDelete) handleDelete(); }}
                style={{ width: '100%', padding: 8, fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }} />
            </div>
            {error && <div style={{ color: '#B85042', fontSize: 12, marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn" onClick={closeDelete} disabled={deleting}>Cancel</button>
              <button className="btn btn-primary" onClick={handleDelete} disabled={!canConfirmDelete || deleting}
                style={{ background: canConfirmDelete && !deleting ? '#B85042' : '#e0a59d', borderColor: '#B85042' }}>
                {deleting ? <><span className="spinner" />Deleting…</> : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      }
    </div>);

}